import * as Yup from 'yup';
import * as yupUtils from 'hellospa/common/utils/yup';
import { defineMessages } from 'react-intl';
import { intl } from 'hellospa/components/hs-intl-provider';
import { Actions } from './index';

const messages = defineMessages({
  recipientName: {
    id: '',
    defaultMessage: 'Recipient name',
    description: 'Label for input fields that gets injected into error messages',
  },
  anEmailAddress: {
    id: '',
    defaultMessage: 'An email address',
    description: 'Label for input fields that gets injected into error messages',
  },
  CCs: {
    id: '',
    defaultMessage: 'CCs',
    description: 'Label for input fields that gets injected into error messages',
  },
  ccRoles: {
    id: '',
    defaultMessage: 'CC roles',
    description: 'Label for input fields that gets injected into error messages',
  },

});

export enum CCTypes {
  CCEmail = 'ccEmail',
  CCRole = 'ccRole',
}

interface BaseCC {
  readonly id: string,
}

export interface CCEmail extends BaseCC {
  readonly type: CCTypes.CCEmail,
  readonly email: string,
  readonly role?: CCRole | null,
}

export interface CCRole extends BaseCC {
  readonly type: CCTypes.CCRole,
  readonly name: string,
}

export type CC =
  | CCEmail
  | CCRole

const ccRoleSchemaDefinition = {
  type: Yup.mixed().oneOf([CCTypes.CCRole]),
  id: Yup.string().required(),
  name: Yup.string().trim().label(intl.formatMessage(messages.recipientName))
    .required()
    .max(128),
};

export const ccRoleSchema = Yup.object<CCRole>(ccRoleSchemaDefinition);

export const ccEmailSchema = Yup.object<CCEmail>({
  type: Yup.mixed().oneOf([CCTypes.CCEmail]),
  id: Yup.string().required(),
  email: Yup.string().trim().label(intl.formatMessage(messages.anEmailAddress))
    .email()
    .max(100),
  role: Yup.object().nullable().shape(ccRoleSchemaDefinition).default(null),
});

export const multiCCEmailSchema = Yup.object().shape({
  ccRecipients: Yup.array()
    .of(ccEmailSchema).label(intl.formatMessage(messages.CCs))
    .unique(['email']),
});

export const meOnlyMultiCCEmailSchema = Yup.object().shape({
  ccRecipients: Yup.array()
    .of(ccEmailSchema.clone().shape({
      email: Yup.string().trim().label(intl.formatMessage(messages.anEmailAddress))
        .email()
        .required()
        .max(100),
    })).label(intl.formatMessage(messages.CCs))
    .unique(['email']),
});

export const multiCCRoleSchema = Yup.object().shape({
  ccRecipients: Yup.array()
    .of(ccRoleSchema).label(intl.formatMessage(messages.ccRoles))
    .unique(['name']),
});

const ccUnionSchema = {
  [CCTypes.CCEmail]: ccEmailSchema,
  [CCTypes.CCRole]: ccRoleSchema,
};
export const ccSchema = yupUtils.lazyUnion<
  yupUtils.InferUnion<typeof ccUnionSchema>,
  'type'
>('type', ccUnionSchema);

export interface CreateCCAction {
  type: Actions.CreateCC
  payload: CC
}

export interface SetCCsAction {
  type: Actions.SetCCs
  payload: CC[]
}

export interface DeleteCCAction {
  type: Actions.DeleteCC
  payload: CC['id']
}

interface BaseUpdate<A extends Actions, T, TKey extends keyof T> {
  type: A,
  payload: {
    id: T[TKey]
    updates: Partial<T>
  }
}

export type UpdateCCAction = BaseUpdate<Actions.UpdateCC, CC, 'id'>

export type CCActions =
  | SetCCsAction
  | CreateCCAction
  | DeleteCCAction
  | UpdateCCAction
